<template>
    <el-dialog
      v-dialog-window
      :title="vehicleEditing ? '修改车辆' : '新增车辆'"
      :visible.sync="dialogVisible"
      width="32rem"
      append-to-body
      :close-on-click-modal="false"
      custom-class="vm-dialog"
    >
      <el-form ref="vehicleFormRef" :model="vehicleForm" :rules="vehicleRules" label-width="6.5rem" label-position="right">
        <el-form-item v-if="!vehicleEditing" label="车辆分组:" prop="groupPath">
          <VehicleTeamTreePicker
            v-model="vehicleForm.groupPath"
            :options="groupPickerTree"
            placeholder="请选择"
            @change="onFormGroupChange"
          />
        </el-form-item>
        <el-form-item label="车牌号:" prop="vehicleNo">
          <PlateKeyboardInput
            v-model="vehicleForm.vehicleNo"
            placeholder="请输入车牌号"
          />
        </el-form-item>
        <el-form-item label="车牌颜色:" prop="plateColor">
          <el-select v-model="vehicleForm.plateColor" placeholder="请选择" style="width: 100%">
            <el-option v-for="o in plateColorOptions" :key="o.value" :label="o.label" :value="o.value" />
            <el-option v-if="hasInactivePlateColor" :label="plateColorLabel(vehicleForm.plateColor)" :value="vehicleForm.plateColor" disabled />
          </el-select>
        </el-form-item>
        <el-form-item label="别名:" prop="aliasName">
          <el-input v-model="vehicleForm.aliasName" placeholder="请输入别名" clearable />
        </el-form-item>
        <el-form-item label="备注手机号:" prop="remarkMobile">
          <el-input v-model="vehicleForm.remarkMobile" placeholder="请输入备注手机号" clearable />
        </el-form-item>
        <el-form-item v-if="vehicleEditing" label="状态:"><el-switch v-model="vehicleForm.enabled" active-text="正常" inactive-text="禁用" /></el-form-item>
      </el-form>
      <span slot="footer" class="vm-dialog__footer">
        <el-button @click="resetVehicleForm"><i class="el-icon-refresh-left"></i> 重置</el-button>
        <el-button v-permission="vehicleForm.id ? 'vehicle:edit' : 'vehicle:add'" type="primary" :loading="submitting" @click="submitVehicle">
          <i class="el-icon-check"></i> 提交
        </el-button>
      </span>
    </el-dialog>
</template>

<script>

import { authState, hasPermission } from '@/utils/auth'
import { loadBusinessOptions, businessOptionLabel } from '@/utils/businessDictionaries'
import VehicleTeamTreePicker from '@/components/business/VehicleTeamTreePicker.vue'
import PlateKeyboardInput from '@/components/PlateKeyboardInput.vue'
import { getVehicleGroups, saveVehicle, updateVehicle, isRequestSuccess } from './lingdongApi'

function emptyVehicleForm() {
  return { id: null, groupId: null, groupPath: [], vehicleNo: '', plateColor: 2, aliasName: '', remarkMobile: '', enabled: true }
}

function findGroupPath(nodes, id, path = []) {
  for (const node of nodes || []) {
    const next = [...path, node.id]
    if (String(node.id) === String(id)) return next
    const found = findGroupPath(node.children, id, next)
    if (found.length) return found
  }
  return []
}

function selectableGroups(nodes) {
  return (nodes || []).filter(node => node.ungrouped || Number(node.status) === 1).map(node => ({ ...node, disabled: false, children: selectableGroups(node.children) }))
}

function buildGroupTree(groups) {
  const nodes = (groups || []).map(item => ({ ...item, id: String(item.id), parentId: String(item.parentId || 0), teamName: item.groupName, children: [] }))
  const map = Object.fromEntries(nodes.map(node => [node.id, node]))
  const roots = []
  nodes.forEach(node => {
    if (node.parentId !== '0' && map[node.parentId]) map[node.parentId].children.push(node)
    else roots.push(node)
  })
  const sort = list => { list.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder)); list.forEach(node => sort(node.children)) }
  sort(roots)
  return [{ id: '0', teamName: '未分组', ungrouped: true, status: 1, children: [] }, ...roots]
}

export default {
  name: 'VehicleFormDialog',
  components: { VehicleTeamTreePicker, PlateKeyboardInput },
  props: {
    visible: Boolean,
    vehicle: { type: Object, default: () => ({}) },
    groupTree: { type: Array, default: null },
    initialGroupId: { type: [String, Number], default: null }
  },
  data() {
    return {
      vehicleForm: emptyVehicleForm(), vehicleSnapshot: null,
      localGroupTree: [], plateColorOptions: [], plateColorLabels: {},
      submitting: false, destroyed: false, dialogSequence: 0,
      vehicleRules: {
        groupPath: [{ required: true, message: '请选择车辆分组', trigger: 'change' }],
        vehicleNo: [{ required: true, message: '请输入车牌号', trigger: 'blur' }],
        plateColor: [{ required: true, message: '请选择车牌颜色', trigger: 'change' }]
      }
    }
  },
  computed: {
    dialogVisible: { get() { return this.visible }, set(value) { this.$emit('update:visible', value) } },
    vehicleEditing() { return this.vehicleForm.id != null },
    managementAccount() { return authState.user },
    groupPickerTree() { return selectableGroups(this.groupTree || this.localGroupTree) },
    hasInactivePlateColor() { return this.vehicleEditing && !this.plateColorOptions.some(item => Number(item.value) === Number(this.vehicleForm.plateColor)) }
  },
  watch: {
    visible: { immediate: true, handler(value) { if (value) this.openForm(); else this.closeForm() } },
    managementAccount() { this.closeForm(); this.dialogVisible = false }
  },
  beforeDestroy() { this.destroyed = true; this.dialogSequence++ },
  methods: {
    openForm() {
      const sequence = ++this.dialogSequence
      const actor = authState.user
      this.submitting = false
      const row = this.vehicle || {}
      this.vehicleForm = row.id != null
        ? { id: row.id, groupId: row.groupId == null ? null : row.groupId, groupPath: [], vehicleNo: row.vehicleNo || '', plateColor: row.plateColor == null ? 2 : Number(row.plateColor), aliasName: row.aliasName || '', remarkMobile: row.remarkMobile || '', enabled: Number(row.status) === 1 && !Number(row.deleted) }
        : emptyVehicleForm()
      const applyGroup = () => {
        if (this.vehicleEditing || this.initialGroupId == null) return
        this.vehicleForm.groupPath = findGroupPath(this.groupPickerTree, this.initialGroupId)
        this.onFormGroupChange(this.vehicleForm.groupPath)
      }
      applyGroup()
      this.vehicleSnapshot = { ...this.vehicleForm, groupPath: [...this.vehicleForm.groupPath] }
      this.plateColorOptions = []
      this.plateColorLabels = {}
      const current = () => !this.destroyed && this.visible && sequence === this.dialogSequence && actor === authState.user
      loadBusinessOptions('vehicle_plate_color').then(options => {
        if (!current()) return
        this.plateColorOptions = options
        this.plateColorLabels = Object.fromEntries(options.map(item => [item.value, item.label]))
        if (!this.vehicleEditing && !options.some(item => Number(item.value) === Number(this.vehicleForm.plateColor))) {
          this.vehicleForm.plateColor = options.length ? options[0].value : null
          this.vehicleSnapshot.plateColor = this.vehicleForm.plateColor
        }
      }).catch(error => { if (current()) this.$message.error(error.msg || error.message || '车牌颜色字典加载失败') })
      if (!this.groupTree) {
        this.localGroupTree = []
        getVehicleGroups().then(res => {
          if (!current()) return
          if (!isRequestSuccess(res)) throw new Error(res.msg || '车辆分组加载失败')
          this.localGroupTree = buildGroupTree(res.data)
          if (!this.vehicleForm.groupPath.length) {
            applyGroup()
            this.vehicleSnapshot.groupPath = [...this.vehicleForm.groupPath]
            this.vehicleSnapshot.groupId = this.vehicleForm.groupId
          }
        }).catch(error => { if (current()) this.$message.error(error.msg || error.message || '车辆分组加载失败') })
      }
      this.$nextTick(() => this.$refs.vehicleFormRef && this.$refs.vehicleFormRef.clearValidate())
    },
    closeForm() { this.dialogSequence++; this.submitting = false },
    plateColorLabel(value) {
      if (!this.plateColorOptions.some(item => Number(item.value) === Number(value)) && this.plateColorLabels[value]) return this.plateColorLabels[value] + '（已停用）'
      return businessOptionLabel('vehicle_plate_color', this.plateColorOptions, value)
    },
    onFormGroupChange(path) { this.vehicleForm.groupId = path && path.length && String(path[path.length - 1]) !== '0' ? path[path.length - 1] : null },
    resetVehicleForm() {
      if (this.vehicleSnapshot) this.vehicleForm = { ...this.vehicleSnapshot, groupPath: [...this.vehicleSnapshot.groupPath] }
      this.$nextTick(() => this.$refs.vehicleFormRef && this.$refs.vehicleFormRef.clearValidate())
    },
    submitVehicle() {
      if (this.submitting) return
      const sequence = this.dialogSequence
      const actor = authState.user
      const current = () => !this.destroyed && this.visible && sequence === this.dialogSequence && actor === authState.user
      return this.$refs.vehicleFormRef.validate(async valid => {
        if (!valid || !current() || this.submitting) return
        if (!hasPermission(this.vehicleEditing ? 'vehicle:edit' : 'vehicle:add')) return this.$message.warning('没有车辆保存权限')
        const unchangedIdentity = this.vehicleEditing && this.vehicleSnapshot && this.vehicleSnapshot.vehicleNo === this.vehicleForm.vehicleNo && Number(this.vehicleSnapshot.plateColor) === Number(this.vehicleForm.plateColor)
        if (!unchangedIdentity && !this.plateColorOptions.some(item => Number(item.value) === Number(this.vehicleForm.plateColor))) return this.$message.warning('请选择启用的车牌颜色')
        if (!this.vehicleEditing && !this.vehicleForm.groupPath.length) return this.$message.warning('请选择车辆分组')
        this.submitting = true
        try {
          const payload = { groupId: this.vehicleForm.groupId, vehicleNo: this.vehicleForm.vehicleNo, plateColor: Number(this.vehicleForm.plateColor), aliasName: this.vehicleForm.aliasName || null, remarkMobile: this.vehicleForm.remarkMobile || null, status: this.vehicleForm.enabled ? 1 : 0 }
          const res = this.vehicleEditing ? await updateVehicle(this.vehicleForm.id, payload) : await saveVehicle(payload)
          if (!current()) return
          if (!isRequestSuccess(res) || !res.data || res.data.id == null) throw new Error(res.msg || '车辆保存结果不完整，请刷新后确认')
          this.$message.success('操作成功')
          this.submitting = false
          this.$emit('saved', res.data)
          this.dialogVisible = false
        } catch (error) { if (current()) this.$message.error(error.msg || error.message || '车辆保存失败') }
        finally { if (current()) this.submitting = false }
      })
    }
  }
}
</script>

<style scoped>
.vm-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
</style>

<style>
.vm-dialog .el-dialog__header {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #eee;
}

.vm-dialog .el-dialog__body {
  padding: 1.25rem 1.5rem 0.5rem;
}

.vm-dialog .el-dialog__footer {
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid #eee;
}

</style>
